import { Button, Stack, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import InvalidUser from "@/components/InvalidUser";
import InviteWorkspaceUsersLink from "@/components/Workspace/InviteWorkspaceUsersLink";
import WorkspaceInvitesTable from "@/components/Workspace/WorkspaceInvitesTable";
import WorkspaceMembersTable from "@/components/Workspace/WorkspaceMembersTable";
import Locator from "@/locator";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { workspaceInviteCountResponseSelect, workspaceMemberCountResponseSelect } from "@/models/repositories";
import UserManagementContextProvider from "@/models/UserManagementContextProvider";
import { UxContext } from "@/models/UxStateProvider";

export default function WorkspaceUserManagementNew({ newDesign }: { newDesign?: boolean }) {
  const { availableWorkspaceRoles } = useContext(DataContext);
  const { setNavTitle } = useContext(UxContext);
  const params = useParams();
  const workspaceId = params?.workspaceId as string;
  const theme = useTheme();

  const [showMembersTable, setShowMembersTable] = useState<boolean>(true);
  const [showPendingInvites, setShowPendingInvites] = useState<boolean>(false);

  const workspaceMemberCountResponse =
    useReactiveSelectQuery(
      workspaceMemberCountResponseSelect,
      [{ workspaceId }],
      "WorkspaceUserManagement:workspaceMemberCountResponseSelect",
    ) ?? [];

  const workspaceInviteCountResponse =
    useReactiveSelectQuery(
      workspaceInviteCountResponseSelect,
      [{ workspaceId }],
      "WorkspaceUserManagement:workspaceInviteCountResponseSelect",
    ) ?? [];

  const workspaceMemberCount = (workspaceMemberCountResponse?.[0]?.memberCount as unknown as number) ?? 0;
  const workspaceInviteCount = (workspaceInviteCountResponse?.[0]?.inviteCount as unknown as number) ?? 0;

  const showMembersView = () => {
    setShowMembersTable(true);
    setShowPendingInvites(false);
  };

  const showInvitesView = () => {
    setShowMembersTable(false);
    setShowPendingInvites(true);
  };

  useEffect(() => {
    setNavTitle("Manage members");
  }, [setNavTitle]);

  const adminRoles = availableWorkspaceRoles?.get("admin")?.role;
  if (adminRoles !== "admin") {
    return <InvalidUser validRoles={[availableWorkspaceRoles?.get("admin")?.role]} />;
  }

  return (
    <Box sx={{ width: "100%", height: "100vh" }}>
      <UserManagementContextProvider>
        <Stack
          sx={{
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            pt: newDesign ? 2 : 8,
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", mb: 2 }}>
            {newDesign && (
              <Typography component="h3" sx={{ fontSize: "1.75rem", fontWeight: 700 }}>
                Manage Users
              </Typography>
            )}
            <Box sx={{ flexGrow: 1, justifyContent: "center", display: "flex", gap: 1 }}>
              <Box>
                <Button
                  sx={{ ...(showMembersTable && { bgcolor: "var(--color-zinc-700)" }) }}
                  onClick={showMembersView}
                  aria-label={Locator.workspaceNav.members.membersTab}
                >
                  {workspaceMemberCount} {workspaceMemberCount === 1 ? "Member" : "Members"}
                </Button>
              </Box>
              <Box>
                <Button
                  sx={{ ...(showPendingInvites && { bgcolor: "var(--color-zinc-700)" }) }}
                  onClick={showInvitesView}
                  aria-label={Locator.workspaceNav.members.pendingInvitesTab}
                >
                  {workspaceInviteCount > 0 ? workspaceInviteCount : ""}
                  {" Pending "}
                  {workspaceInviteCount === 1 ? "invite" : "invites"}
                </Button>
              </Box>
            </Box>
            <Box>
              <InviteWorkspaceUsersLink sx={{ minWidth: 200 }} />
            </Box>
          </Box>

          <Box sx={{ ...(newDesign && { width: "calc(100vw - 90px)" }) }}>
            {showMembersTable && !showPendingInvites ? <WorkspaceMembersTable newDesign={newDesign} /> : null}
            {!showMembersTable && showPendingInvites ? <WorkspaceInvitesTable newDesign={newDesign} /> : null}
          </Box>
        </Stack>
      </UserManagementContextProvider>
    </Box>
  );
}
